
import {Injectable, isDevMode} from '@angular/core';
import {AngularFireDatabase} from "@angular/fire/compat/database";
import {AngularFirestore} from "@angular/fire/compat/firestore";
import {where} from "@angular/fire/firestore";
import {environment} from "../../environments/environment";
import {IpService} from "./ip.service";

export interface ActivityLog {
  date: number,
  section: string,
  action: string,
  user?: string,
  description: string
  currentIp: string
}

@Injectable({
  providedIn: 'root'
})
export class LogService {
  env = isDevMode() ? environment.testMode : environment.productionMode
  currentIp: string = '';
  private ipReadyPromise: Promise<string> | null = null;
  private retryAttempts = 0;
  private retryDelay = 1000; // Start with 1 second
  private maxRetryDelay = 10000; // Max 10 seconds between retries

  constructor(
    private readonly mDatabase: AngularFireDatabase,
    private readonly mFirestore: AngularFirestore,
    private ipService: IpService
  ) {
    // Initialize IP fetch immediately
    this.initializeIp();
  }

  private initializeIp(): void {
    // Check localStorage first
    const saved = localStorage.getItem('currentip');
    if (saved && this.isValidIp(saved)) {
      this.currentIp = saved;
      console.log("✅ Valid IP found in storage:", saved);
      return;
    }

    // Clear invalid IP from storage
    if (saved === '0.0.0.0' || !saved || saved === 'null') {
      localStorage.removeItem('currentip');
      console.log("🗑️ Cleared invalid IP from storage");
    }

    // Start fetching valid IP
    console.log("🔄 No valid IP found. Starting fetch process...");
    this.fetchValidIp();
  }

  private fetchValidIp(): void {
    this.retryAttempts++;
    const attemptNum = this.retryAttempts;

    console.log(`🔄 Attempt ${attemptNum}: Fetching IP address...`);

    this.ipService.getIpAddress().subscribe({
      next: (res: any) => {
        if (res && res.ip && this.isValidIp(res.ip)) {
          this.currentIp = res.ip;
          localStorage.setItem('currentip', res.ip);
          console.log(`✅ SUCCESS! Valid IP obtained: ${res.ip}`);
          this.retryAttempts = 0;
          this.retryDelay = 1000;
        } else {
          console.error(`❌ Attempt ${attemptNum}: Invalid IP response:`, res);
          this.scheduleRetry();
        }
      },
      error: (err) => {
        console.error(`❌ Attempt ${attemptNum}: IP fetch failed:`, err.message || err);
        this.scheduleRetry();
      }
    });
  }

  private scheduleRetry(): void {
    // Exponential backoff with jitter
    const jitter = Math.random() * 1000; // 0-1 second random delay
    const backoffDelay = Math.min(
      this.retryDelay * Math.pow(1.5, Math.min(this.retryAttempts - 1, 5)),
      this.maxRetryDelay
    );
    const totalDelay = backoffDelay + jitter;

    console.log(`⏳ Retry scheduled in ${(totalDelay / 1000).toFixed(1)}s (Attempt ${this.retryAttempts})`);

    setTimeout(() => {
      console.log(`🔁 Retrying now... (Attempt ${this.retryAttempts + 1})`);
      this.fetchValidIp();
    }, totalDelay);
  }

  // Strict IP validation
  private isValidIp(ip: string): boolean {
    if (!ip || ip === '0.0.0.0' || ip === 'null' || ip === 'undefined' || ip.trim() === '') {
      console.log(`❌ IP validation failed: "${ip}" is not valid`);
      return false;
    }

    // IPv4 validation
    const ipv4Regex = /^(\d{1,3}\.){3}\d{1,3}$/;
    if (ipv4Regex.test(ip)) {
      const parts = ip.split('.');
      const isValid = parts.every(part => {
        const num = parseInt(part, 10);
        return num >= 0 && num <= 255;
      });

      if (!isValid) {
        console.log(`❌ IP validation failed: "${ip}" has invalid octets`);
        return false;
      }

      // Additional check: reject 0.0.0.0
      if (ip === '0.0.0.0') {
        console.log(`❌ IP validation failed: 0.0.0.0 is not allowed`);
        return false;
      }

      return true;
    }

    // IPv6 validation (simplified)
    const ipv6Regex = /^([0-9a-fA-F]{0,4}:){2,7}[0-9a-fA-F]{0,4}$/;
    if (ipv6Regex.test(ip)) {
      return true;
    }

    console.log(`❌ IP validation failed: "${ip}" doesn't match IPv4 or IPv6 format`);
    return false;
  }

  // Wait for valid IP - BLOCKS until IP is obtained
  private waitForValidIp(): Promise<string> {
    // If already have valid IP, return immediately
    if (this.currentIp && this.isValidIp(this.currentIp)) {
      console.log(`✅ Using existing valid IP: ${this.currentIp}`);
      return Promise.resolve(this.currentIp);
    }

    // If already waiting, return existing promise
    if (this.ipReadyPromise) {
      console.log(`⏳ Already waiting for IP...`);
      return this.ipReadyPromise;
    }

    // Create new promise that waits for valid IP
    console.log(`⏳ Waiting for valid IP address...`);
    this.ipReadyPromise = new Promise((resolve, reject) => {
      let checkCount = 0;
      const maxChecks = 120; // 60 seconds (500ms * 120)

      const checkIp = () => {
        checkCount++;

        if (this.currentIp && this.isValidIp(this.currentIp)) {
          console.log(`✅ Valid IP obtained after ${checkCount * 0.5}s: ${this.currentIp}`);
          this.ipReadyPromise = null;
          resolve(this.currentIp);
        } else if (checkCount >= maxChecks) {
          console.error(`❌ Timeout: No valid IP after ${maxChecks * 0.5}s`);
          this.ipReadyPromise = null;
          reject(new Error('Unable to obtain IP address. Please check your internet connection and try again.'));
        } else {
          // Check again in 500ms
          if (checkCount % 10 === 0) {
            console.log(`⏳ Still waiting for IP... (${checkCount * 0.5}s elapsed)`);
          }
          setTimeout(checkIp, 500);
        }
      };

      checkIp();
    });

    return this.ipReadyPromise;
  }

  async addLog(activity: ActivityLog) {
    try {
      console.log(`📝 Attempting to add log for action: ${activity.action}`);

      // BLOCK until we have valid IP
      const validIp = await this.waitForValidIp();

      const email = localStorage.getItem('userEmail') || 'Unknown';
      activity.user = email;
      activity.description = activity.description + ' ' + email;
      activity.currentIp = validIp; // Use the validated IP

      // Double-check before pushing
      if (!this.isValidIp(activity.currentIp)) {
        throw new Error('CRITICAL: Invalid IP detected before logging. Aborting.');
      }

      await this.mDatabase.list('activityLog').push(activity);
      console.log(`✅ Log added successfully with IP: ${activity.currentIp}`);

    } catch (error: any) {
      console.error('❌ FAILED to add log:', error.message);
      throw new Error(`Cannot record activity: ${error.message}`);
    }
  }

  async addInventoryLog(log: any) {
    try {
      console.log(`📦 Attempting to add inventory log...`);

      // REMOVE any existing invalid IP
      if (log.currentIp && !this.isValidIp(log.currentIp)) {
        console.warn(`⚠️ Removing invalid IP "${log.currentIp}" from inventory log`);
        delete log.currentIp;
      }

      // BLOCK until we have valid IP
      const validIp = await this.waitForValidIp();

      const email = localStorage.getItem('userEmail') || 'Unknown';
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      const userName = userData.userName || '';

      const logData = {
        ...log,
        submittedBy: userName,
        email: email,
        currentIp: validIp,
        date: new Date()
      };

      // Double-check before adding
      if (!this.isValidIp(logData.currentIp)) {
        throw new Error('CRITICAL: Invalid IP detected before logging. Aborting.');
      }

      await this.mFirestore.collection(this.env.inventoryLogs).add(logData);
      console.log(`✅ Inventory log added with IP: ${logData.currentIp}`);

    } catch (error: any) {
      console.error('❌ FAILED to add inventory log:', error.message);
      throw new Error(`Cannot record inventory activity: ${error.message}`);
    }
  }

  // Public method to check IP status
  getIpStatus(): { ip: string, isValid: boolean } {
    const isValid = this.isValidIp(this.currentIp);
    return {
      ip: this.currentIp || 'Fetching...',
      isValid: isValid
    };
  }

  // Manual refresh method
  async refreshIp(): Promise<string> {
    console.log("🔄 Manual IP refresh requested");
    this.currentIp = '';
    localStorage.removeItem('currentip');
    this.retryAttempts = 0;
    this.retryDelay = 1000;
    this.ipReadyPromise = null;

    this.fetchValidIp();
    return await this.waitForValidIp();
  }

  getLogs() {
    return this.mDatabase.list<ActivityLog>('activityLog').valueChanges()
  }

  getLogsByCount(i: number) {
    return this.mDatabase.list<ActivityLog>('activityLog', ref => ref.limitToLast(i)).query.once('value')
  }

  getLogsByDateRange(start: number, end: number) {
    return this.mDatabase.list<ActivityLog>('activityLog', ref =>
      ref.orderByChild('date').startAt(start).endAt(end)
    ).query.once('value')
  }

  addCounsellingLog(value: any) {
    return this.addLogWithIp(this.env.counsellingLogs, value);
  }

  addEducationLog(value: any) {
    return this.addLogWithIp(this.env.educationLogs, value);
  }

  // Generic method to add logs with IP validation
  private async addLogWithIp(collection: string, value: any): Promise<any> {
    try {
      console.log(`📝 Adding log to ${collection}...`);

      // REMOVE any existing invalid IP from the value object
      if (value.currentIp) {
        if (!this.isValidIp(value.currentIp)) {
          console.warn(`⚠️ Removing invalid IP "${value.currentIp}" from log data`);
          delete value.currentIp;
        }
      }

      // WAIT for valid IP
      const validIp = await this.waitForValidIp();

      // Add valid IP to the log data
      const logDataWithIp = {
        ...value,
        currentIp: validIp,
        timestamp: new Date()
      };

      // Double-check IP is valid
      if (!this.isValidIp(logDataWithIp.currentIp)) {
        throw new Error('CRITICAL: Invalid IP detected. Aborting log.');
      }

      const result = await this.mFirestore.collection(collection).add(logDataWithIp);
      console.log(`✅ Log added to ${collection} with IP: ${validIp}`);

      return result;

    } catch (error: any) {
      console.error(`❌ Failed to add log to ${collection}:`, error.message);
      throw new Error(`Cannot record activity: ${error.message}`);
    }
  }

  async updateCounsellingLog(value: any) {
    try {
      const validIp = await this.waitForValidIp();
      const updateData = {
        ...value,
        currentIp: validIp,
        updatedAt: new Date()
      };
      return this.mFirestore.collection(this.env.counsellingLogs).doc(value.id).update(updateData);
    } catch (error: any) {
      console.error('❌ Failed to update counselling log:', error.message);
      throw error;
    }
  }

  async updateEducationLog(value: any) {
    try {
      const validIp = await this.waitForValidIp();
      const updateData = {
        ...value,
        currentIp: validIp,
        updatedAt: new Date()
      };
      return this.mFirestore.collection(this.env.educationLogs).doc(value.id).update(updateData);
    } catch (error: any) {
      console.error('❌ Failed to update education log:', error.message);
      throw error;
    }
  }

  deleteLog(id: string) {
    return this.mFirestore.collection(this.env.counsellingLogs).doc(id).delete()
  }

  deleteEducationLog(id: string) {
    return this.mFirestore.collection(this.env.educationLogs).doc(id).delete()
  }

  fetchCounsellingLog(selectedCentre: string) {
    let q = this.mFirestore.collection(this.env.counsellingLogs, ref => ref.where('centreCode', '==', selectedCentre)).valueChanges({idField: 'id'})

    if (selectedCentre == "All") q = this.mFirestore.collection(this.env.counsellingLogs).valueChanges({idField: 'id'})
    return q
  }

  fetchEducationLogs(selectedCentre: string) {
    let q = this.mFirestore.collection(this.env.educationLogs, ref => ref.where('centreCode', '==', selectedCentre)).valueChanges({idField: 'id'})

    if (selectedCentre == "All") q = this.mFirestore.collection(this.env.educationLogs).valueChanges({idField: 'id'})
    return q
  }

  updateStatus(element: any) {
    let status = element.status == 'Pending' ? 'Verified' : 'Pending'
    return this.mFirestore.collection(this.env.counsellingLogs).doc(element.id).update({status: status})
  }

  updateEducationStatus(element: any) {
    let status = element.status == 'Pending' ? 'Verified' : 'Pending'
    return this.mFirestore.collection(this.env.educationLogs).doc(element.id).update({status: status})
  }

  async updateSessionLog(sessionName: string, status: any, remark: string, reviewedBy: string) {
    try {
      const validIp = await this.waitForValidIp();
      return this.mFirestore.doc(sessionName).update({
        status: status,
        reviewRemark: remark,
        reviewedBy: reviewedBy,
        currentIp: validIp,
        updatedAt: new Date()
      });
    } catch (error: any) {
      console.error('❌ Failed to update session log:', error.message);
      throw error;
    }
  }
}
